import type { RichNode } from '@cyphex/types';
import { parseInline } from './inline';
import type { MentionTarget } from './mentions';

export const FENCE = /^```([A-Za-z0-9+#-]*)\s*$/;
const HEADING = /^(#{1,3})\s+(.*)$/;
const BULLET = /^[-*+]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;
const QUOTE = /^>\s?(.*)$/;
const RULE = /^(?:-{3,}|\*{3,})$/;

export type ListKind = 'bullet_list' | 'ordered_list';

/** A line that becomes exactly one block on its own. `null` = it is body text. */
export function standaloneBlock(
  line: string,
  targets: readonly MentionTarget[] = [],
): RichNode | null {
  if (RULE.test(line.trim())) return { type: 'horizontal_rule' };

  const heading = HEADING.exec(line);
  if (heading) {
    return {
      type: 'heading',
      attrs: { level: (heading[1] ?? '#').length },
      content: parseInline(heading[2] ?? '', 0, targets),
    };
  }

  const quote = QUOTE.exec(line);
  if (quote) {
    return {
      type: 'blockquote',
      content: [{ type: 'paragraph', content: parseInline(quote[1] ?? '', 0, targets) }],
    };
  }

  return null;
}

/** A list line. The caller merges consecutive items of the same kind. */
export function listLine(
  line: string,
  targets: readonly MentionTarget[] = [],
): { kind: ListKind; item: RichNode } | null {
  const bullet = BULLET.exec(line);
  const ordered = bullet ? null : ORDERED.exec(line);
  const text = bullet?.[1] ?? ordered?.[1];
  if (text === undefined) return null;
  return {
    kind: bullet ? 'bullet_list' : 'ordered_list',
    item: {
      type: 'list_item',
      content: [{ type: 'paragraph', content: parseInline(text, 0, targets) }],
    },
  };
}
